/**
 * useKeyboardShortcuts - Keyboard shortcut management for Planner
 * 
 * Created: 08-10-2025 13:47:25
 * Purpose: Centralized keyboard shortcut handling for power users
 * 
 * Shortcuts:
 * - Ctrl+F: Focus filters panel
 * - Ctrl+N: Open new assignment modal
 * - Ctrl+E: Open export modal
 * - Ctrl+P: Open publish modal
 * - Esc: Close all modals
 * 
 * Features:
 * - Prevents conflicts with browser shortcuts
 * - Respects modal focus (Esc only works when modals open)
 * - Accessible (doesn't interfere with screen readers)
 * - Configurable callbacks
 * 
 * Usage:
 * ```typescript
 * useKeyboardShortcuts({
 *   onOpenFilters: () => setFiltersOpen(true),
 *   onNewAssignment: () => setShowAssignModal(true),
 *   onExport: () => setShowExportModal(true),
 *   onPublish: () => setShowPublishModal(true),
 *   onCloseModals: () => closeAllModals(),
 *   enabled: true
 * });
 * ```
 * 
 * @module hooks/useKeyboardShortcuts
 */

import { useHotkeys } from 'react-hotkeys-hook';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

export interface KeyboardShortcutsConfig {
  /** Callback when Ctrl+F is pressed (focus filters) */
  onOpenFilters?: () => void;
  
  /** Callback when Ctrl+N is pressed (new assignment) */
  onNewAssignment?: () => void;
  
  /** Callback when Ctrl+E is pressed (export) */
  onExport?: () => void;
  
  /** Callback when Ctrl+P is pressed (publish) */
  onPublish?: () => void;
  
  /** Callback when Esc is pressed (close modals) */
  onCloseModals?: () => void;
  
  /** Enable/disable all shortcuts (default: true) */
  enabled?: boolean;
}

export interface ShortcutInfo {
  /** Key combination */
  keys: string;
  
  /** Description of what the shortcut does */
  description: string;
  
  /** Whether the shortcut is currently enabled */
  enabled: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Shortcut definitions for help modal / documentation */
export const SHORTCUT_DEFINITIONS: Omit<ShortcutInfo, 'enabled'>[] = [
  {
    keys: 'Ctrl+F',
    description: 'Open/focus filters panel'
  },
  {
    keys: 'Ctrl+N',
    description: 'Create new assignment'
  },
  {
    keys: 'Ctrl+E',
    description: 'Export planner data'
  },
  {
    keys: 'Ctrl+P',
    description: 'Publish schedule'
  },
  {
    keys: 'Esc',
    description: 'Close open modals'
  }
];

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

/**
 * useKeyboardShortcuts Hook
 * 
 * Registers keyboard shortcuts for planner operations using react-hotkeys-hook.
 * Prevents default browser behavior for conflicting shortcuts.
 * 
 * @param config - Configuration object with callback functions
 */
export function useKeyboardShortcuts(config: KeyboardShortcutsConfig): void {
  const {
    onOpenFilters,
    onNewAssignment,
    onExport,
    onPublish,
    onCloseModals,
    enabled = true
  } = config;

  // Ctrl+F - Focus filters
  // Prevent browser find dialog
  useHotkeys(
    'ctrl+f',
    (event) => {
      event.preventDefault();
      onOpenFilters?.();
    },
    {
      enabled: enabled && !!onOpenFilters,
      enableOnFormTags: false, // Don't trigger when typing in inputs
      description: 'Open filters panel'
    },
    [onOpenFilters, enabled]
  );

  // Ctrl+N - New assignment
  // Prevent browser new window
  useHotkeys(
    'ctrl+n',
    (event) => {
      event.preventDefault();
      onNewAssignment?.();
    },
    {
      enabled: enabled && !!onNewAssignment,
      enableOnFormTags: false,
      description: 'Create new assignment'
    },
    [onNewAssignment, enabled]
  );

  // Ctrl+E - Export
  // Prevent potential browser conflicts
  useHotkeys(
    'ctrl+e',
    (event) => {
      event.preventDefault();
      onExport?.();
    },
    {
      enabled: enabled && !!onExport,
      enableOnFormTags: false,
      description: 'Export planner data'
    },
    [onExport, enabled]
  );

  // Ctrl+P - Publish
  // Prevent browser print dialog
  useHotkeys(
    'ctrl+p',
    (event) => {
      event.preventDefault();
      onPublish?.();
    },
    {
      enabled: enabled && !!onPublish,
      enableOnFormTags: false,
      description: 'Publish schedule'
    },
    [onPublish, enabled]
  );

  // Esc - Close modals
  // Allow on form tags since it's for closing modals
  useHotkeys(
    'escape',
    (event) => {
      // Don't prevent default - let other Esc handlers work
      onCloseModals?.();
    },
    {
      enabled: enabled && !!onCloseModals,
      enableOnFormTags: true, // Allow in forms to close modals
      description: 'Close open modals'
    },
    [onCloseModals, enabled]
  );
}

/**
 * Get all available shortcuts with their current enabled state
 * 
 * Useful for displaying keyboard shortcuts in a help modal or settings panel.
 * 
 * @param config - Current keyboard shortcuts configuration
 * @returns Array of shortcut information
 */
export function getAvailableShortcuts(config: KeyboardShortcutsConfig): ShortcutInfo[] {
  const { onOpenFilters, onNewAssignment, onExport, onPublish, onCloseModals, enabled = true } = config;

  return SHORTCUT_DEFINITIONS.map((def) => ({
    ...def,
    enabled: enabled && (
      (def.keys === 'Ctrl+F' && !!onOpenFilters) ||
      (def.keys === 'Ctrl+N' && !!onNewAssignment) ||
      (def.keys === 'Ctrl+E' && !!onExport) ||
      (def.keys === 'Ctrl+P' && !!onPublish) ||
      (def.keys === 'Esc' && !!onCloseModals)
    )
  }));
}

